import { createSimpleEvent, SimpleEvent } from '@passionware/simple-event';
import { ensureError } from '@passionware/platform-js';
import { z } from 'zod';
import {
  isApiConnectionError,
  isSerializedConnectionFailure
} from 'lib/api/apiClientLifecycle';

export type ServerFailureVariant = 'bootstrap' | 'session' | 'sign_in';

export type ServerFailureKind = 'connection' | 'server';

export type ServerFailurePayload = {
  error: Error;
  variant: ServerFailureVariant;
  kind: ServerFailureKind;
};

export const serverUnreachableEvent: SimpleEvent<ServerFailurePayload> =
  createSimpleEvent<ServerFailurePayload>();

export function emitServerUnreachable(
  error: unknown,
  variant: ServerFailureVariant
): void {
  const normalizedError = toError(error);
  serverUnreachableEvent.emit({
    error: normalizedError,
    variant,
    kind: classifyServerFailureKind(error, normalizedError)
  });
}

function classifyServerFailureKind(
  error: unknown,
  normalizedError: Error
): ServerFailureKind {
  if (
    isSerializedConnectionFailure(error) ||
    isSerializedConnectionFailure(normalizedError)
  ) {
    return 'connection';
  }

  if (hasExplicitTransportFailure(error)) {
    return 'connection';
  }

  if (hasConnectionErrorMessage(normalizedError.message)) {
    return 'connection';
  }

  return 'server';
}

function hasConnectionErrorMessage(message: string): boolean {
  return (
    message.includes('Failed to fetch') ||
    message.includes('Request has been terminated') ||
    message.includes('ECONNREFUSED') ||
    message.includes('ENOTFOUND') ||
    message.includes('ETIMEDOUT')
  );
}

const explicitTransportFailure$ = z.object({
  status: z.number().optional(),
  code: z.string().min(1).optional(),
  timeout: z.boolean().optional(),
  message: z.string().optional()
});

const errorWithMessage$ = z.object({
  message: z.string()
});

function hasExplicitTransportFailure(error: unknown): boolean {
  const parsed = explicitTransportFailure$.safeParse(error);
  if (!parsed.success || !isApiConnectionError(error)) {
    return false;
  }

  const err = parsed.data;

  if (err.status === 0 || err.timeout === true) {
    return true;
  }

  if (err.code) {
    return true;
  }

  if (err.message) {
    return hasConnectionErrorMessage(err.message);
  }

  return false;
}

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  const parsed = errorWithMessage$.safeParse(error);
  if (parsed.success) {
    return ensureError(parsed.data.message);
  }

  return ensureError(typeof error === 'string' ? error : 'Server unreachable');
}
